import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import _ from "lodash";

import { ModalOverlay } from "../styles";
import {
  UpdateModalContentWrapper,
  UpdateModalHeaderWrapper,
  UpdateModalWrapper,
  AccountBlock,
  AccountFullName,
  UpdateTab,
  UpdateTabs,
  UpdateModalFooterWrapper,
} from "./styles";
import { Button } from "../../Button";
import { CheckCircle } from "react-bootstrap-icons";
import { Essentials } from "./Essentials";
import { ContactDetails } from "./ContactDetails";
import { IProfile } from "../../../types/profile";
import { requestUpdateProfile } from "../../../store/slices/profiles";
import { getModalUser } from "../../../store/selectors/modals";
import { AvatarCard } from "../../AvatarCard";
import { getAvatarPath } from "../../../helpers/getAvatarPath";
import { requestAllUsers, setUserById } from "../../../store/slices/users";
import { getAllUsers, getUserById } from "../../../store/selectors/users";
import { getTeamUsers } from "../../../store/selectors/teams";
import { useLocation } from "react-router-dom";
import { requestTeamUsersList } from "../../../store/slices/teams";
import { IUser } from "../../../models/Users";

export const profileValidationSchema = () => {
  return Yup.object().shape({
    firstName: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Please enter your First Name")
      .matches(/^[a-zA-Z]+$/, "First Name should only contain letters"),

    lastName: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Please enter your Last Name")
      .matches(/^[a-zA-Z]+$/, "Last Name should only contain letters"),

    jobTitle: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Please enter your Job Title")
      .matches(/^[a-zA-Z]+$/, "Job Title should only contain letters"),

    organization: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Please enter your Organization"),

    location: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Please enter your Location"),
  });
};

export const UpdateProfileModal = ({ onClose }: { onClose: () => void }) => {
  const location = useLocation();
  const user = useSelector(getModalUser);
  const users = useSelector(getAllUsers);
  const teamUsers = useSelector(getTeamUsers);
  const userProfile = useSelector(getUserById);
  const [selectedTab, setTab] = useState<string>("essentials");

  const dispatch = useDispatch();

  const fullName = useMemo(() => {
    const name = [user?.profile?.firstName, user?.profile?.lastName]
      ?.join(" ")
      ?.trim();
    if (name?.length) {
      return name;
    }
    if (user?.name) {
      return user?.name;
    }
    return "Not set";
  }, [user?.name, user?.profile?.firstName, user?.profile?.lastName]);

  const initials = useMemo(() => {
    let initials = user?.email?.charAt(0);
    if (user?.profile?.firstName?.length || user?.profile?.lastName?.length) {
      initials =
        (user?.profile?.firstName?.charAt(0) || "") +
        (user?.profile?.lastName?.charAt(0) || "");
    }
    if (user?.name?.length) {
      initials = user?.name?.charAt(0);
    }
    return initials?.toUpperCase();
  }, [
    user?.email,
    user?.name,
    user?.profile?.firstName,
    user?.profile?.lastName,
  ]);

  const handleSubmitForm = (values: IProfile) => {
    dispatch(
      requestUpdateProfile({
        ...values,
        avatar: getAvatarPath(values.avatar),
        callbacks: {
          onSuccess: () => {
            onClose();
            if (location.pathname.includes("/teams/")) {
              const id = location.pathname.split("/")?.at(3);
              dispatch(
                requestTeamUsersList({
                  id: id as string,
                  limit: teamUsers?.length,
                })
              );
            } else {
              dispatch(requestAllUsers({ limit: users?.length }));

              dispatch(
                setUserById({
                  ...userProfile,
                  profile: { ...userProfile.profile, ...values },
                } as IUser)
              );
            }
          },
        },
      })
    );
  };

  return (
    <ModalOverlay onClick={onClose}>
      <UpdateModalWrapper onClick={(e) => e.stopPropagation()}>
        <UpdateModalHeaderWrapper>
          <AccountBlock>
            <AvatarCard avatar={user?.profile?.avatar} initials={initials} />
            <AccountFullName>{fullName}</AccountFullName>
          </AccountBlock>
          <UpdateTabs>
            <UpdateTab
              $selected={selectedTab === "essentials"}
              onClick={() => setTab("essentials")}
            >
              Essentials
            </UpdateTab>
            <UpdateTab
              $selected={selectedTab === "contact_details"}
              onClick={() => setTab("contact_details")}
            >
              Contact details
            </UpdateTab>
          </UpdateTabs>
        </UpdateModalHeaderWrapper>
        <Formik
          initialValues={{
            ...user?.profile,
            email: user?.profile?.email || user?.email,
            firstName: user?.profile?.firstName || user?.name,
          }}
          validationSchema={profileValidationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmitForm}
        >
          {({
            values,
            setValues,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => {
            return (
              <>
                <UpdateModalContentWrapper>
                  <Form onSubmit={handleSubmit}>
                    {selectedTab === "essentials" && (
                      <Essentials
                        values={values}
                        touched={touched}
                        errors={errors}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        initials={initials}
                        avatar={user?.profile?.avatar}
                      />
                    )}
                    {selectedTab === "contact_details" && (
                      <ContactDetails
                        values={values}
                        touched={touched}
                        errors={errors}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                    )}
                  </Form>
                </UpdateModalContentWrapper>
                <UpdateModalFooterWrapper>
                  <Button
                    name="Cancel"
                    onClick={onClose}
                    variant="neutral"
                    size="medium"
                  />
                  <Button
                    name="Update"
                    onClick={() => {
                      handleSubmitForm(values);
                    }}
                    disabled={_.isEqual(values, user?.profile)}
                    variant={
                      _.isEqual(values, user?.profile)
                        ? "disabled"
                        : "secondary"
                    }
                    size="medium"
                    icon={<CheckCircle />}
                  />
                </UpdateModalFooterWrapper>
              </>
            );
          }}
        </Formik>
      </UpdateModalWrapper>
    </ModalOverlay>
  );
};
